import { PrestazioneData } from 'utils/interfaces';

export interface PrestazioneItemConfig {
  key: keyof PrestazioneData;
  label: string;
  textKey?: keyof PrestazioneData;
  textPlaceholder?: string;
  noCheckbox?: boolean;
  // Rendered on the right edge of the primary row instead of the indented children row below it —
  // e.g. "CER ___" / "FIR ___", which the paper form prints flush-right on the same line as the
  // checkbox they belong to. Only meaningful for non-primary (index > 0) items.
  alignEnd?: boolean;
}

export interface PrestazioneGroupConfig {
  title: string;
  items: PrestazioneItemConfig[];
  // No parent/child split: every item renders as an equal, compact checkbox in one wrapped row
  // (the paper form prints these as flat checklists, not a "heading + sub-options" block).
  flatList?: boolean;
}

export const PRESTAZIONE_GROUPS: PrestazioneGroupConfig[] = [
  {
    title: 'Disostruzione condotta orizzontale',
    items: [
      { key: 'disostOrizz', label: 'Disostruzione condotta orizzontale' },
      { key: 'disostOrizzCucina', label: 'Cucina' },
      { key: 'disostOrizzWc', label: 'WC' },
      { key: 'disostOrizzPluviale', label: 'Pluviale' },
      { key: 'disostOrizzAltro', label: 'Altro', textKey: 'disostOrizzAltroTesto' },
    ],
  },
  {
    title: 'Disostruzione periodica pozzetto sifonato',
    items: [
      { key: 'disostPozzettoSifonato', label: 'Disostruzione periodica pozzetto sifonato' },
    ],
  },
  {
    title: 'Disostruzione condotta verticale',
    items: [
      { key: 'disostVert', label: 'Disostruzione condotta verticale' },
      { key: 'disostVertCucina', label: 'Cucina' },
      { key: 'disostVertWc', label: 'WC' },
      { key: 'disostVertPluviale', label: 'Pluviale' },
      { key: 'disostVertAltro', label: 'Altro', textKey: 'disostVertAltroTesto' },
    ],
  },
  {
    // CER codes are the waste codes generated by a septic-tank service, so they render at the same
    // level as Settica/Disoleatore/Pompe di sollevamento rather than as their own separate group.
    title: 'Bonifica vasca',
    items: [
      { key: 'bonificaVasca', label: 'Bonifica vasca' },
      { key: 'bonificaVascaSettica', label: 'Settica' },
      { key: 'bonificaVascaDisoleatore', label: 'Disoleatore' },
      { key: 'bonificaVascaPompeSollevamento', label: 'Pompe di sollevamento' },
      { key: 'cer200304', label: 'CER 20 03 04' },
      { key: 'cer161002', label: 'CER 16 10 02' },
      { key: 'cer070612', label: 'CER 07 06 12' },
      { key: 'cerAltro', label: 'CER', textKey: 'cerAltroValore', textPlaceholder: 'Altro codice' },
    ],
  },
  {
    title: 'Trasporto',
    flatList: true,
    items: [
      { key: 'trasporto', label: 'Trasporto', textKey: 'trasportoDescr', textPlaceholder: 'Trasporto' },
      { key: 'trasporto', label: 'CER', noCheckbox: true, textKey: 'trasportoCer', textPlaceholder: 'Codice CER' },
    ],
  },
  {
    // "FIR" is trasportoFir — same field as before, only its printed position moves from the
    // Trasporto line to the Smaltimento line, matching the paper form's layout.
    title: 'Smaltimento presso impianto',
    items: [
      { key: 'smaltimentoImpianto', label: 'Smaltimento presso impianto', textKey: 'smaltimentoImpiantoNome', textPlaceholder: 'Nome impianto' },
      { key: 'trasporto', label: 'FIR', textKey: 'trasportoFir', textPlaceholder: 'N. FIR', noCheckbox: true, alignEnd: true },
    ],
  },
  {
    title: 'Disinfestazione',
    items: [
      { key: 'disinfestazione', label: 'Disinfestazione' },
      { key: 'disinfestazioneZanzare', label: 'Zanzare' },
      { key: 'disinfestazioneBlatte', label: 'Blatte' },
      { key: 'disinfestazioneFormiche', label: 'Formiche' },
      { key: 'disinfestazioneAltro', label: 'Altro', textKey: 'disinfestazioneAltroTesto' },
    ],
  },
  {
    title: 'Derattizzazione',
    items: [
      { key: 'derattizzazione', label: 'Derattizzazione' },
      { key: 'derattizzazione', label: 'N. erogatori', textKey: 'derattizzazioneNErogatori', textPlaceholder: 'N.', noCheckbox: true },
      { key: 'derattizzazione', label: 'Monitoraggio', textKey: 'derattizzazioneMonitoraggio', textPlaceholder: 'Note', noCheckbox: true },
    ],
  },
  {
    title: 'Videoispezione',
    items: [
      { key: 'videoispezione', label: 'Videoispezione' },
      { key: 'videoispezioneOrizzontale', label: 'Orizzontale' },
      { key: 'videoispezioneVerticale', label: 'Verticale' },
    ],
  },
  {
    title: 'Pulizia',
    items: [
      { key: 'pulizia', label: 'Pulizia' },
      { key: 'sanificazione', label: 'Sanificazione' },
      { key: 'disinfezione', label: 'Disinfezione' },
    ],
  },
  {
    title: 'Consegna / Pulizia WC / Ritiro',
    flatList: true,
    items: [
      { key: 'consegna', label: 'Consegna N.', textKey: 'consegnaNumero', textPlaceholder: 'N.' },
      { key: 'puliziaWc', label: 'Pulizia N. WC', textKey: 'puliziaWcNumero', textPlaceholder: 'N.' },
      { key: 'ritiro', label: 'Ritiro N.', textKey: 'ritiroNumero', textPlaceholder: 'N.' },
    ],
  },
  {
    title: 'Altro',
    items: [
      { key: 'altroPrestazione', label: 'Altro', textKey: 'altroPrestazioneTesto', textPlaceholder: 'Descrizione' },
    ],
  },
];

export const EMPTY_PRESTAZIONE: PrestazioneData = {
  disostOrizz: false, disostOrizzCucina: false, disostOrizzWc: false,
  disostOrizzPluviale: false, disostOrizzAltro: false, disostOrizzAltroTesto: null,

  disostPozzettoSifonato: false,

  disostVert: false, disostVertCucina: false, disostVertWc: false,
  disostVertPluviale: false, disostVertAltro: false, disostVertAltroTesto: null,

  bonificaVasca: false, bonificaVascaSettica: false,
  bonificaVascaDisoleatore: false, bonificaVascaPompeSollevamento: false,

  cer200304: false, cer161002: false, cer070612: false,
  cerAltro: false, cerAltroValore: null,

  trasporto: false, trasportoDescr: null, trasportoCer: null, trasportoFir: null,

  smaltimentoImpianto: false, smaltimentoImpiantoNome: null,

  disinfestazione: false, disinfestazioneZanzare: false, disinfestazioneBlatte: false,
  disinfestazioneFormiche: false, disinfestazioneAltro: false, disinfestazioneAltroTesto: null,

  derattizzazione: false, derattizzazioneNErogatori: null, derattizzazioneMonitoraggio: null,

  videoispezione: false, videoispezioneOrizzontale: false, videoispezioneVerticale: false,

  pulizia: false, sanificazione: false, disinfezione: false,

  consegna: false, consegnaNumero: null,
  puliziaWc: false, puliziaWcNumero: null,
  ritiro: false, ritiroNumero: null,

  altroPrestazione: false, altroPrestazioneTesto: null,
};
